import CommentData from '../types/commentData';
import NotificationApi from '../api/notificationApi';
import SkillApi from '../api/skillApi';
import { NewSectionCommentData, NewSkillCommentData, NotificationData } from '../types/notification';
import SectionApi from 'api/sectionApi';
import UserProfileApi from '../api/userProfileApi';

export default class NotificationService {
  private static readonly endpoint = `${process.env.REACT_APP_NOTIFICATION_SERVER_URL}/notifications`;

  private static async sendNotifications(notifications: NotificationData[]) {
    await fetch(this.endpoint, {
      headers: { 'Content-Type': 'application/json' },
      method: 'post',
      body: JSON.stringify(notifications),
    });
  }

  private static async sendNotification(notification: NotificationData) {
    await this.sendNotifications([notification]);
  }

  public static async createAndSendSkillQuestionNotification(comment: CommentData) {
    const skill = await SkillApi.getSkillById(comment.contextId);
    const notification = {
      userId: skill.authorUuid,
      actorId: comment.userId,
      read: false,
      type: 'NEW_SKILL_COMMENT' as const,
      data: {
        skillId: skill.id,
        commentId: comment.id,
      },
    };
    const id = await NotificationApi.addNotification(notification);
    await this.sendNotification({ ...notification, id });
  }

  public static async createAndSendSectionQuestionNotification(comment: CommentData) {
    const section = await SectionApi.getSectionById(comment.contextId);
    const skill = await SkillApi.getSkillById(section.skillId);
    const notification = {
      userId: skill.authorUuid,
      actorId: comment.userId,
      read: false,
      type: 'NEW_SECTION_COMMENT' as const,
      data: {
        sectionId: section.id,
        commentId: comment.id,
      },
    };
    const id = await NotificationApi.addNotification(notification);
    await this.sendNotification({ ...notification, id });
  }

  public static async markNotificationAsReadInArray(notification: NotificationData, notifications: NotificationData[]) {
    const id = await NotificationApi.updateNotification({ ...notification, read: true });
    const updatedNotification = await NotificationApi.getNotification(id);
    return notifications.map(notification =>
      notification.id === updatedNotification.id ? updatedNotification : notification
    );
  }

  public static async markAllNotificationsAsReadInArray(notifications: NotificationData[]) {
    await NotificationApi.setAllNotificationsToRead(notifications);
    return notifications.map(notification => ({ ...notification, read: true }));
  }

  // Notification text resolvers
  public static async resolveNotificationText(notification: NotificationData): Promise<[string, string, string]> {
    switch (notification.type) {
      case 'NEW_SKILL_COMMENT':
        return await this.resolveNewSkillCommentText(notification.data, notification.actorId);
      case 'NEW_SECTION_COMMENT':
        return this.resolveNewSectionCommentText(notification.data, notification.actorId);
    }
  }

  public static async resolveNewSkillCommentText(
    { skillId, commentId }: NewSkillCommentData,
    actorId: string | null
  ): Promise<[string, string, string]> {
    const commentingUserProfile = actorId ? await UserProfileApi.getUserProfileByUuid(actorId) : null;
    const skill = await SkillApi.getSkillById(skillId);
    const message = `${
      commentingUserProfile ? commentingUserProfile.firstName ?? 'User' : 'An anonymous user'
    } commented on your skill ${skill.name}`;
    const title = 'New comment';
    const link = `/skills/${skill.slug}#discussion,comment:${commentId}`;
    return [message, title, link];
  }

  public static async resolveNewSectionCommentText(
    { sectionId, commentId }: NewSectionCommentData,
    actorId: string | null
  ): Promise<[string, string, string]> {
    const commentingUserProfile = actorId ? await UserProfileApi.getUserProfileByUuid(actorId) : null;
    const section = await SectionApi.getSectionById(sectionId);
    const skill = await SkillApi.getSkillById(section.skillId);
    const message = `${
      commentingUserProfile ? commentingUserProfile.firstName ?? 'User' : 'An anonymous user'
    } commented on your section ${section.name}`;
    const title = 'New comment';
    const link = `/skills/${skill.slug}/sections/${section.slug}#discussion,comment:${commentId}`;
    return [message, title, link];
  }
}
